import React, { useEffect } from 'react';
import { Card } from '../components/Card';
import { Header } from '../components/Header';
import { Icon } from '../components/Icon';
import { formatCurrency } from '../utils/format';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { useTranslation } from 'react-i18next';
import { AddLimitFlow, LimitData } from '../components/AddLimitFlow';
import { Limit } from '../types';
import { useDialog } from '../hooks/useDialog';
import { useWebPowerSyncLimits } from '../hooks/usePowerSync';
import './GoalsScreen.css';

interface LimitsScreenProps {
  isEmbedded?: boolean;
}

export const LimitsScreen: React.FC<LimitsScreenProps> = ({ isEmbedded = false }) => {
  const { setLoading, settings } = useAppStore();
  const { t } = useTranslation();
  const { showAlert, showConfirm, DialogComponent } = useDialog();
  
  // PowerSync данные
  const { limits: psLimits } = useWebPowerSyncLimits();
  const [isPremiumRequired, setIsPremiumRequired] = React.useState(false);
  
  // Limits state
  const [limits, setLimits] = React.useState<Limit[]>([]);
  const [monthlyExpenses, setMonthlyExpenses] = React.useState<Record<string, number>>({});
  const [availableCategories, setAvailableCategories] = React.useState<string[]>([]);
  const [showAddLimitModal, setShowAddLimitModal] = React.useState(false);
  const [editingLimit, setEditingLimit] = React.useState<Limit | null>(null);
  const [isLoadingLimits, setIsLoadingLimits] = React.useState(true); // Локальное состояние загрузки

  // Функция для получения короткого лейбла периода
  const getPeriodLabel = (months: number): string => {
    switch(months) {
      case 1: return '1 мес';
      case 3: return '3 мес';
      case 6: return '6 мес';
      case 12: return '1 год';
      default: return `${months} мес`;
    }
  };

  // Синхронизируем PowerSync лимиты
  useEffect(() => {
    if (psLimits.length > 0) {
      setLimits(psLimits.map((l: any) => ({
        ...l,
        limit_amount: l.limit_amount,
        spent: l.spent || 0,
        remaining: l.remaining || 0,
        percentage: l.percentage || 0,
      })));
    }
  }, [psLimits]);

  useEffect(() => {
    loadLimits();
    
    // Слушаем событие открытия модального окна
    const handleOpenModal = () => setShowAddLimitModal(true);
    window.addEventListener('openAddLimitModal', handleOpenModal);
    
    return () => {
      window.removeEventListener('openAddLimitModal', handleOpenModal);
    };
  }, []);

  const loadLimits = async () => {
    // Если PowerSync данные доступны, используем их
    if (psLimits.length > 0) {
      console.log('📏 [PowerSync] Using PowerSync limits');
      setLimits(psLimits.map((l: any) => ({
        ...l,
        limit_amount: l.limit_amount,
        spent: l.spent || 0,
        remaining: l.remaining || 0,
        percentage: l.percentage || 0,
      })));
      const expObj: Record<string, number> = {};
      psLimits.forEach((l: any) => {
        if (l.category_name) expObj[l.category_name] = l.spent || 0;
      });
      setMonthlyExpenses(expObj);
      setIsLoadingLimits(false);
      return;
    }
    
    const startTime = Date.now();
    try {
      setIsLoadingLimits(true);
      setIsPremiumRequired(false);
      console.log('⏳ [LIMITS] Starting to load limits from API (fallback)...');
      
      const data = await apiClient.getLimitsAndGoalsData();
      const loadTime = Date.now() - startTime;
      console.log(`✅ [LIMITS] Loaded limits data in ${loadTime}ms:`, data);
      
      setLimits(data.limits || []);
      
      // Преобразуем массив monthly_expenses в объект { category: amount }
      const expensesObj: Record<string, number> = {};
      if (Array.isArray(data.monthly_expenses)) {
        data.monthly_expenses.forEach((expense: any) => {
          expensesObj[expense.category] = expense.amount;
        });
      }
      console.log('📊 Monthly expenses converted:', expensesObj);
      setMonthlyExpenses(expensesObj);
      
      setAvailableCategories(data.available_categories_for_limit || []);
      
      if (loadTime > 5000) {
        console.warn(`⚠️ [LIMITS] Slow loading detected: ${loadTime}ms`);
      }
    } catch (error: any) {
      console.error('❌ [LIMITS] Error loading limits:', error);
      if (error?.message?.includes('Premium required')) {
        setIsPremiumRequired(true);
      }
    } finally {
      setIsLoadingLimits(false);
    }
  };

  const handleAddLimit = async (limitData: LimitData) => {
    try {
      setLoading(true);
      await apiClient.setLimit(limitData.category, limitData.amount, limitData.period_months);
      await loadLimits();
      setShowAddLimitModal(false);
      setEditingLimit(null);
      // Показываем alert после закрытия модалки
      setTimeout(() => {
        showAlert('Лимит установлен!', { type: 'success', title: 'Успешно' });
      }, 300);
    } catch (error: any) {
      console.error('Error setting limit:', error);
      setShowAddLimitModal(false);
      setEditingLimit(null);
      // Показываем alert после закрытия модалки
      setTimeout(() => {
        showAlert(error?.message || 'Не удалось установить лимит. Попробуйте ещё раз.', { 
          type: 'error', 
          title: 'Ошибка' 
        });
      }, 300);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLimit = async (categoryName: string, periodMonths: number) => {
    const confirmed = await showConfirm(
      `Удалить лимит для категории "${categoryName}"?`,
      { title: 'Подтверждение', confirmText: 'Удалить', cancelText: 'Отмена' }
    );
    
    if (!confirmed) return;

    try {
      setLoading(true);
      await apiClient.deleteLimit(categoryName, periodMonths as 1 | 3 | 6 | 12);
      await loadLimits();
      // Показываем alert после обновления данных
      setTimeout(() => {
        showAlert('Лимит удалён!', { type: 'success', title: 'Успешно' });
      }, 300);
    } catch (error: any) {
      console.error('Error deleting limit:', error);
      // Показываем alert с задержкой
      setTimeout(() => {
        showAlert(error?.message || 'Не удалось удалить лимит.', { 
          type: 'error', 
          title: 'Ошибка' 
        });
      }, 300);
    } finally {
      setLoading(false);
    }
  };

  const getLimitProgress = (limit: Limit) => {
    const spent = monthlyExpenses[limit.category_name] || 0;
    const percentage = (spent / limit.limit_amount) * 100;
    return { spent, percentage: Math.min(percentage, 100) };
  };

  const getLimitStatus = (percentage: number) => {
    if (percentage >= 100) {
      return { status: 'exceeded', text: 'Превышен', color: '#FF3B30' };
    } else if (percentage >= 70) {
      return { status: 'approaching', text: 'Близко к лимиту', color: '#FF9500' };
    }
    return { status: 'ok', text: 'В пределах', color: '#34C759' };
  };

  return (
    <div className="goals-screen page">
      {!isEmbedded && <Header title={t('limits.title') || 'Лимиты расходов'} />}
      
      {isPremiumRequired ? (
        <div className="section">
          <Card padding="large">
            <div className="empty-state">
              <div className="empty-state__icon">
                <Icon name="star" size={48} color="#FF9500" />
              </div>
              <div className="empty-state__title">Требуется Premium</div>
              <div className="empty-state__subtitle">
                Лимиты расходов доступны только для Premium пользователей
              </div>
            </div>
          </Card>
        </div>
      ) : (
        <div className="section">
          {/* Add Limit Button */}
          {!isPremiumRequired && !isLoadingLimits && availableCategories.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <button
                className="limits-add-button neo-raised"
                onClick={() => {
                  setEditingLimit(null);
                  setShowAddLimitModal(true);
                }}
              >
                Установить лимит
              </button>
            </div>
          )}

          {isLoadingLimits ? (
            <Card padding="large" className="neo-static">
              <div className="empty-state">
                <div className="empty-state__icon">
                  <Icon name="clock" size={64} color="#007AFF" />
                </div>
                <div className="empty-state__title">Загрузка лимитов...</div>
                <div className="empty-state__subtitle">Пожалуйста, подождите</div>
              </div>
            </Card>
          ) : limits.length > 0 ? (
            <div className="limits-list">
              {limits.map((limit) => {
                const { spent, percentage } = getLimitProgress(limit);
                const status = getLimitStatus(percentage);

                return (
                  <Card key={limit.id} padding="small" className="limit-card neo-raised neo-static">
                    <div className="limit-card__header">
                      <div className="limit-card__name">{limit.category_name}</div>
                      <div className="limit-card__amounts">
                        <span className={`limit-card__spent ${status.status}`}>
                          {formatCurrency(spent, settings?.currency || 'RUB')}
                        </span>
                        <span className="limit-card__separator">/</span>
                        <span className="limit-card__limit">
                          {formatCurrency(limit.limit_amount, settings?.currency || 'RUB')}
                        </span>
                        <span className="limit-card__period">
                          / {getPeriodLabel(limit.period_months)}
                        </span>
                      </div>
                    </div>

                    <div className="limit-card__progress-container">
                      <div className="limit-card__progress-bar">
                        <div 
                          className="limit-card__progress-fill"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: status.color
                          }}
                        />
                      </div>
                    </div>

                    <div className="limit-card__footer">
                      <button 
                        className="limit-card__action neo-raised"
                        onClick={() => {
                          setEditingLimit(limit);
                          setShowAddLimitModal(true);
                        }}
                        title="Изменить"
                      >
                        <Icon name="edit" size={18} color="#007AFF" />
                      </button>
                      <button 
                        className="limit-card__action limit-card__action--danger neo-raised"
                        onClick={() => handleDeleteLimit(limit.category_name, limit.period_months)}
                        title="Удалить"
                      >
                        <Icon name="delete" size={18} color="#FF3B30" />
                      </button>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card padding="large" className="neo-static">
              <div className="empty-state">
                <div className="empty-state__icon">
                  <Icon name="alert-circle" size={64} color="#8E8E93" />
                </div>
                <div className="empty-state__title">{t('limits.noLimits')}</div>
                <div className="empty-state__subtitle">Установите лимиты для контроля расходов</div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Add Limit Flow */}
      {showAddLimitModal && !isPremiumRequired && (
        <AddLimitFlow
          onClose={() => {
            setShowAddLimitModal(false);
            setEditingLimit(null);
          }}
          onSubmit={handleAddLimit}
          availableCategories={availableCategories}
          existingLimits={limits.map(l => ({
            category_name: l.category_name,
            limit_amount: l.limit_amount,
            period_months: l.period_months as 1 | 3 | 6 | 12
          }))}
          editingLimit={editingLimit}
          currency={settings?.currency}
          currencyCode={settings?.currency_code}
        />
      )}

      {showAddLimitModal && isPremiumRequired && (
        <div 
          className="modal-overlay modal-overlay--center"
          style={{
            padding: '16px'
          }}
          onClick={() => setShowAddLimitModal(false)}
        >
          <Card padding="large">
            <div className="empty-state">
              <div className="empty-state__icon">
                <Icon name="star" size={48} color="#FF9500" />
              </div>
              <div className="empty-state__title">Требуется Premium</div>
              <div className="empty-state__subtitle">
                Для создания лимитов необходима Premium подписка
              </div>
              <Button variant="primary" onClick={() => setShowAddLimitModal(false)}>
                Понятно
              </Button>
            </div>
          </Card>
        </div>
      )}

      <DialogComponent />
    </div>
  );
};
